import React from 'react'

import {
  usePrice,
  useProductState,
  Link,
  useProductFunctions,
  useConfigState,
  useGlobalLabels,
  useLocalLabels,
} from '@sentecacommerce-theme/lib'
import { ProductPageLabels } from '../../productPage.props'
import { Price, Typography } from '../../../Common'
import { ProductRating } from '../ProductRating'
import styled from 'styled-components'

export const ProductInfo = ({ isMobile = false }: { isMobile?: boolean }) => {
  const { selectedVariant, metaData } = useProductState()
  const { getCustomFieldValue } = useProductFunctions()
  const config = useConfigState()
  const { sku } = useGlobalLabels()
  const labels = useLocalLabels<ProductPageLabels>()
  const offer = selectedVariant?.offer
  const hideRegularPrice = getCustomFieldValue(
    config.customFieldKeys.hideRegularPrice
  )
  const hidePromoLabel = getCustomFieldValue(
    config.customFieldKeys.hidePromoLabel
  )
  const hidePrice = selectedVariant?.quantity === 0
  const isInAction = getCustomFieldValue(config.customFieldKeys.isAction)

  const offerValidFrom =
    selectedVariant?.offer?.discount &&
    new Date(
      selectedVariant?.offer?.discount?.validFrom ?? ''
    ).toLocaleDateString('bg-BG')

  const offerValidTo =
    selectedVariant?.offer?.discount &&
    new Date(
      selectedVariant?.offer?.discount?.validTo ?? ''
    ).toLocaleDateString('bg-BG')

  const hasExpirableOffer = Boolean(offerValidTo && offerValidFrom)

  const getPrice = () => {
    if (!hideRegularPrice) return offer?.originalPrice

    if (offer?.discountedPrice?.centAmount) return offer?.discountedPrice

    return offer?.originalPrice
  }
  const { price, discountPrice, discountPercentage } = usePrice({
    price: getPrice(),
    discountedPrice: !hideRegularPrice ? offer?.discountedPrice : undefined,
  })

  const hasBrand = Boolean(metaData.brand)

  return (
    <Container isMobile={isMobile}>
      <InfoContainer>
        {hasBrand ? (
          <>
            <Title
              color="silverTree"
              as={hasBrand ? 'h2' : 'h1'}
              variant="5"
              className="mb-3"
              underLineOnHover
            >
              <Link href={`/${metaData.brand?.slug}/b`}>
                <a>{metaData.brand?.name}</a>
              </Link>
            </Title>
            <Title
              color="greyTitle"
              className="mb-1"
              as="h1"
              variant="3"
              fontWeight={500}
            >
              {metaData.name}
            </Title>
          </>
        ) : (
          <Title
            color="greyTitle"
            className="mb-1"
            as="h1"
            variant="3"
            fontWeight={500}
          >
            {metaData.name}
          </Title>
        )}

        <RatingContainer>
          <ProductRating
            value={metaData.reviewRatingStatistics?.averageRating}
            productId={metaData?._id as string}
          />
        </RatingContainer>
        <SKU>{`${sku.toUpperCase()}: ${selectedVariant?.sku}`}</SKU>
        {!hidePrice && (
          <PriceContainer>
            <Price
              price={price.value}
              discountPercentage={discountPercentage}
              discountedPrice={discountPrice ? discountPrice.value : undefined}
              currency={price.currency}
              size="small"
              hideRegularPrice={hideRegularPrice}
              hidePromoLabel={hidePromoLabel}
              hideDiscountPercentage={isInAction}
              isProductPage
            />
          </PriceContainer>
        )}
        {hasExpirableOffer && (
          <OfferTimestampContainer>
            {' '}
            <Typography variant="8" color="greyTitle">
              {`${labels?.offerValidSentence?.start} ${offerValidFrom} ${labels?.offerValidSentence?.end} ${offerValidTo}`}
            </Typography>
          </OfferTimestampContainer>
        )}
      </InfoContainer>
    </Container>
  )
}

const OfferTimestampContainer = styled.div.attrs({ className: 'd-flex' })`
  width: 100%;
`

const Container = styled.div.attrs({
  className: 'd-flex justify-content-between',
})<{ isMobile?: boolean }>``

const Title = styled(Typography).attrs({
  'data-blogger': '',
})<any>`
  line-height: 1.1;
  font-weight: ${(props) => props.fontWeight || '500'};

  & > a:hover {
    opacity: 0.7;
    text-decoration: ${(props) =>
      props.underLineOnHover ? 'underline' : 'none'};
  }
`

const SKU = styled(Typography).attrs({
  variant: '8',
  color: 'greyTitle',
})<any>`
  opacity: 0.8;
`

const InfoContainer = styled.div`
  width: 100%;
`

const PriceContainer = styled(Typography).attrs({
  className: 'pt-2 mt-3 pb-2 d-block',
  color: 'greyTitle',
  as: 'span',
  variant: 3,
})<any>`
  font-weight: 550;
  & div {
    text-align: left;
  }
`

const RatingContainer = styled.div.attrs({
  className: 'mb-2',
})`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  & div {
    margin: 0;
  }
`
